import { memo, useMemo, useState } from 'react';
import { DateRange } from 'react-aria-components';

import { Button, Flex, ListBox, ListBoxItem, Popover, Select, SelectValue, Table, Text } from '@/components/core';
import { ChevronIcon } from '@/components/icons';
import { FieldLogo } from '@/components/logos';
import { useGetAgentFromPath } from '@/hooks';
import { toModelId, useGetModel } from '@/hooks/api';
import { findModelId } from '@/utils';

import { BadgeCell } from './BadgeCell';
import { useStatistics } from './useStatistics';
import { createMetric, getData, statisticToName } from './utils';

const METRIC_TYPE_VALUES = ['automation', 'flagged', 'edits'] as const;
type MetricType = (typeof METRIC_TYPE_VALUES)[number];

export type FieldBreakdownProps = {
  dateRange: DateRange | null;
  dateSelection: number | string;
  isEmpty?: boolean;
};

const IFieldBreakdown = ({ dateRange, dateSelection, isEmpty }: FieldBreakdownProps) => {
  const ColumnHeader = ({ header }: { header: string }) => {
    return (
      <div className="px-4 py-2">
        <Text className="!text-xs font-medium text-gray-500">{header}</Text>
      </div>
    );
  };

  const [selectedMetric, setSelectedMetric] = useState<MetricType>('automation');

  const columns = useMemo(() => {
    return [
      {
        header: () => <ColumnHeader header="Field breakdown" />,
        accessorKey: 'id',
        cell: ({ row }) => {
          const [name, subName] = row.original.name.split(' / ');

          return (
            <Flex variant="plain" className="m-4 items-center gap-2">
              {subName ? (
                <>
                  <Text className="text-gray-400">{name} /</Text>
                  <Text>{subName}</Text>
                </>
              ) : (
                <Text>{name}</Text>
              )}
            </Flex>
          );
        },
        size: 400,
      },
      {
        header: () => (
          <Select
            value={selectedMetric}
            onChange={(metric) => {
              setSelectedMetric((metric?.toString() as MetricType | undefined) ?? 'automation');
            }}
          >
            <div>
              <Button variant="plain">
                <SelectValue />
                <ChevronIcon rotation="down" />
              </Button>
            </div>

            <Popover>
              <ListBox className="max-h-64 overflow-y-auto">
                {METRIC_TYPE_VALUES.map((metric) => (
                  <ListBoxItem key={metric} id={metric}>
                    <Text>{statisticToName[metric]}</Text>
                  </ListBoxItem>
                ))}
              </ListBox>
            </Popover>
          </Select>
        ),
        accessorKey: selectedMetric,
        cell: ({ row }) => {
          switch (selectedMetric) {
            case 'automation':
              return (
                <div className="m-4">
                  <BadgeCell
                    value={row.original.automation.value}
                    tooltip={`${row.original.automation.datapoints} ${row.original.name} values (${(row.original.automation.value * 100).toFixed(1)}%) were automatically validated without requiring human review.`}
                    isEmpty={isEmpty}
                  />
                </div>
              );
            case 'flagged':
              return (
                <div className="m-4">
                  <BadgeCell
                    value={row.original.flagged.value}
                    tooltip={`${row.original.flagged.datapoints} ${row.original.name} values (${(row.original.flagged.value * 100).toFixed(1)}%) were flagged for human review because the agent's confidence was below the set threshold or validation checks failed.`}
                    lowerIsBetter
                    isEmpty={isEmpty}
                  />
                </div>
              );
            case 'edits':
              return (
                <div className="m-4">
                  <BadgeCell
                    value={row.original.edits.value}
                    tooltip={`${row.original.edits.datapoints} ${row.original.name} values (${(row.original.edits.value * 100).toFixed(1)}%) were corrected by a human during review.`}
                    lowerIsBetter
                    isEmpty={isEmpty}
                  />
                </div>
              );
            default:
              return null;
          }
        },
      },
    ];
  }, [isEmpty, selectedMetric]);

  const { data: agent } = useGetAgentFromPath();
  const { data: model } = useGetModel(toModelId(findModelId(agent)));

  const { statistics, previousStatistics } = useStatistics({ dateRange, dateSelection });

  const rows = useMemo(() => {
    const data = [];

    for (const [fieldId, config] of Object.entries(model?.fieldConfig ?? {})) {
      if (config.type === 'table') {
        for (const [subFieldId, subConfig] of Object.entries(config.fields ?? {})) {
          const id = `${fieldId}/${subFieldId}`;
          const subFieldData = getData(statistics?.statistics.fields[id]);
          const previousSubFieldData = getData(previousStatistics?.statistics.fields[id]);

          data.push({
            id,
            automation: createMetric(
              subFieldData.numValuesAutomated,
              subFieldData.numValues,
              previousSubFieldData.numValuesAutomated,
              previousSubFieldData.numValues
            ),
            flagged: createMetric(
              subFieldData.numValuesFlagged,
              subFieldData.numValues,
              previousSubFieldData.numValuesFlagged,
              previousSubFieldData.numValues
            ),
            edits: createMetric(
              subFieldData.numValuesEdited,
              subFieldData.numValues,
              previousSubFieldData.numValuesEdited,
              previousSubFieldData.numValues
            ),
            ...subConfig,
            name: `${config.name} / ${subConfig.name}`,
          });
        }
      } else {
        const fieldData = getData(statistics?.statistics.fields[fieldId]);
        const previousFieldData = getData(previousStatistics?.statistics.fields[fieldId]);

        data.push({
          id: fieldId,
          automation: createMetric(
            fieldData.numValuesAutomated,
            fieldData.numValues,
            previousFieldData.numValuesAutomated,
            previousFieldData.numValues
          ),
          flagged: createMetric(
            fieldData.numValuesFlagged,
            fieldData.numValues,
            previousFieldData.numValuesFlagged,
            previousFieldData.numValues
          ),
          edits: createMetric(
            fieldData.numValuesEdited,
            fieldData.numValues,
            previousFieldData.numValuesEdited,
            previousFieldData.numValues
          ),
          ...config,
        });
      }
    }
    return data;
  }, [model?.fieldConfig, statistics?.statistics.fields, previousStatistics?.statistics.fields]);

  return (
    <Table
      className="w-full overflow-auto"
      props={{
        row: { className: 'border-b border-gray-200' },
        header: { className: 'rounded-lg bg-gray-50 border-b border-gray-200' },
      }}
      data={rows}
      columns={columns}
      hideDragHandle
      hideRowOptions
    />
  );
};

export const FieldBreakdown = memo(IFieldBreakdown) as typeof IFieldBreakdown;
